import React from 'react';
import type { SortType } from 'rsuite-table';

/** Shape every server-paginated admin list endpoint returns. */
export interface ResourceListResult<T> {
  page: number;
  totalPages: number;
  totalAmount: number;
  data: T[];
}

/** Pagination + sort state handed to the fetcher on every load. */
export interface ResourceListQuery {
  page: number;
  perPage: number;
  sortColumn: string;
  sortOrder: string;
}

interface UseResourceListOptions<T> {
  /**
   * Loads one page of results. Receives the current pagination + sort and
   * should close over the page's own filter state.
   */
  fetch: (query: ResourceListQuery) => Promise<ResourceListResult<T>>;
  /**
   * Filter values — a change to any of them re-fetches from page 1
   * (debounced). List them so the hook knows when to reload.
   */
  filters: React.DependencyList;
  /** Debounce applied to filter/sort-driven reloads, in ms. Default 350. */
  debounceMs?: number;
  /** Rows per page initially. Default 15. */
  initialPerPage?: number;
  /** Column the list is sorted by initially; omit for lists without sorting. */
  initialSortColumn?: string;
  /** Sort direction initially. Default 'desc'. */
  initialSortOrder?: SortType;
}

/**
 * Encapsulates the boilerplate every server-paginated admin list page
 * repeats: pagination, sorting, a debounced fetch that reloads when filters
 * change, loading state and stale-response guarding.
 *
 * The page keeps its own filter state and supplies a `fetch` that closes
 * over it; the hook owns pagination, sort, the result set and loading.
 *
 * @param options
 */
export function useResourceList<T>({
  fetch,
  filters,
  debounceMs = 350,
  initialPerPage = 15,
  initialSortColumn = '',
  initialSortOrder = 'desc',
}: UseResourceListOptions<T>) {
  const [perPage, setPerPage] = React.useState(initialPerPage);
  const [sortColumn, setSortColumn] = React.useState(initialSortColumn);
  const [sortType, setSortType] = React.useState<SortType>(initialSortOrder);
  const [result, setResult] = React.useState<ResourceListResult<T>>({
    page: 1,
    totalPages: 0,
    totalAmount: 0,
    data: [],
  });
  const [loading, setLoading] = React.useState(false);

  // Keep the latest fetcher current without making it an effect dependency —
  // the page rebuilds it every render as its filter state changes.
  const fetchRef = React.useRef(fetch);
  fetchRef.current = fetch;

  // Monotonic token so responses from superseded requests are discarded.
  const requestIdRef = React.useRef(0);

  const load = React.useCallback(
    async (page: number = 1) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      try {
        const next = await fetchRef.current({
          page,
          perPage,
          sortColumn,
          sortOrder: sortType.toUpperCase(),
        });
        if (requestId === requestIdRef.current) {
          setResult(next);
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [perPage, sortColumn, sortType]
  );

  // First load runs immediately; later filter / sort / page-size changes are
  // debounced so rapid typing collapses into a single request.
  const isFirstLoadRef = React.useRef(true);
  React.useEffect(() => {
    if (isFirstLoadRef.current) {
      isFirstLoadRef.current = false;
      load(1);
      return;
    }
    const timer = setTimeout(() => load(1), debounceMs);
    return () => clearTimeout(timer);
  }, [load, debounceMs, ...filters]);

  const setSort = React.useCallback((column: string, type: SortType) => {
    setSortColumn(column);
    setSortType(type);
  }, []);

  const reload = React.useCallback(
    () => load(result.page),
    [load, result.page]
  );

  return {
    data: result.data,
    page: result.page,
    totalPages: result.totalPages,
    totalAmount: result.totalAmount,
    loading,
    perPage,
    setPerPage,
    sortColumn,
    sortType,
    setSort,
    /** Fetch a specific page — wire to the pagination's onChangePage. */
    goToPage: load,
    /** Re-fetch the current page (e.g. after a create or delete). */
    reload,
  };
}
